import { FieldErrors } from 'react-hook-form'
import get from 'lodash/get'
import { ICarrierApplicationEntityTypes } from 'types/graphqlTypes'
import * as yup from 'yup'

// regex used to check for leading zeros, and US and Canada Zip Codes
const MC_DOT_REGEX = new RegExp('^([1-9][0-9]*)$')
const US_ZIP_CODE_REGEX = new RegExp('^\\d{5}$')
const CA_ZIP_CODE_REGEX = new RegExp('^[A-Za-z]\\d[A-Za-z]\\d[A-Za-z]\\d$')

const LEADING_ZEROS_MESSAGE = 'cannot begin with zeros'
const MAX_LENGTH_MESSAGE = 'cannot be longer than 8 characters'
const REQUIRED_MESSAGE = 'This field is required'
const INVALID_EMAIL_MESSAGE = 'Must be a valid email'
const US_ZIP_CODE_MESSAGE = 'Please enter a valid US zip code'
const CA_ZIP_CODE_MESSAGE = 'Please enter a valid Canada zip code'

const USZipSchema = yup
  .string()
  .test(
    'regex-us',
    `${US_ZIP_CODE_MESSAGE}`,
    zipcode => !zipcode || US_ZIP_CODE_REGEX.test(zipcode)
  )
  .required(US_ZIP_CODE_MESSAGE)

const CAZipSchema = yup
  .string()
  .test(
    'regex-ca',
    `${CA_ZIP_CODE_MESSAGE}`,
    zipcode => !zipcode || CA_ZIP_CODE_REGEX.test(zipcode)
  )
  .required(CA_ZIP_CODE_MESSAGE)

export const CarrierApplicationSchema = yup.object({
  name: yup.string().required(REQUIRED_MESSAGE),
  dbaName: yup.string(),
  mcNumber: yup
    .string()
    .test(
      'regex-leading-zeros',
      `MC Number ${LEADING_ZEROS_MESSAGE}`,
      mcNumber => !mcNumber || MC_DOT_REGEX.test(mcNumber)
    )
    .max(8, `MC Number ${MAX_LENGTH_MESSAGE}`),
  ein: yup
    .string()
    .test('empty-check', 'EIN must be at least 9 characters', ein => !ein || ein.length >= 9),
  entityType: yup
    .mixed<ICarrierApplicationEntityTypes | null>()
    .oneOf([...Object.values(ICarrierApplicationEntityTypes), null]),
  dotNumber: yup
    .string()
    .test(
      'regex-leading-zeros',
      `DOT Number ${LEADING_ZEROS_MESSAGE}`,
      dotNumber => !dotNumber || MC_DOT_REGEX.test(dotNumber)
    )
    .max(8, `DOT Number ${MAX_LENGTH_MESSAGE}`)
    .required(REQUIRED_MESSAGE),
  isDisadvantagedBusinessEntity: yup.boolean(),
  headquarter: yup.object({
    address: yup.string().required(REQUIRED_MESSAGE),
    countryCode: yup.string().required(REQUIRED_MESSAGE),
    city: yup.string().required(REQUIRED_MESSAGE),
    stateCode: yup.string().required(REQUIRED_MESSAGE),
    zipcode: yup
      .string()
      .required(REQUIRED_MESSAGE)
      .when('countryCode', {
        is: value => value === 'US',
        then: USZipSchema,
        otherwise: CAZipSchema,
      }),
    contactPhone: yup.string().required(REQUIRED_MESSAGE),
    contactPhoneExt: yup.string(),
  }),
  contactEmail: yup.string().email(INVALID_EMAIL_MESSAGE),
  accountingEmail: yup.string().email(INVALID_EMAIL_MESSAGE),
  secondaryAccountingEmail: yup.string().email(INVALID_EMAIL_MESSAGE),
})

export type TCarrierApplicationSchema = yup.InferType<typeof CarrierApplicationSchema>

export const getErrorMessage = <
  T extends FieldErrors<TCarrierApplicationSchema>,
  K extends keyof T
>(
  errors: T,
  keyName: K
): string | undefined => {
  return get(errors, [keyName, 'message'])
}

export const getHQErrorMessage = <
  T extends FieldErrors<TCarrierApplicationSchema>,
  K extends keyof T
>(
  errors: T,
  keyName: K
): string | undefined => {
  const headquarterErrors = get(errors, 'headquarter')
  return get(headquarterErrors, [keyName, 'message'])
}
